import { apiSlice } from './api-slice';
import {
  BasicInfo,
  ChangedStatus,
  Company,
  ContactInfo,
  TeamMember,
  PaginatedCompany,
  Response,
  SubscriptionPlan,
  SubscriptionPlanName,
  Sortable,
  Role,
} from '../models';
import { size } from '../constants';

export const companyApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAllCompanies: builder.query<
      Response<Sortable<Company>>,
      PaginatedCompany
    >({
      query: ({ page, name, subscriptionPlan }) => ({
        url: `basicInfo/fetchAll`,
        method: 'GET',
        // Only send name/subscriptionPlan when set. An empty name string still
        // routes the backend to its criteria-filter query; omitting it falls
        // back to the simpler unfiltered listing for the default dashboard view.
        params: {
          size,
          page,
          ...(name ? { name } : {}),
          ...(subscriptionPlan ? { subscriptionPlan } : {}),
        },
      }),
    }),
    getCompanyById: builder.query<Response<Company>, number>({
      query: companyId => ({
        // FetchCompanyController.findCustomizeCompanyResultById — returns name,
        // status, subscriptionPlan, companyType, numberOfProjects, companyAvatar.
        url: `company/${companyId}`,
        method: 'GET',
      }),
      providesTags: [
        { type: 'Company', id: 'subscriptionPlan' },
        { type: 'Company', id: 'basicInfo' },
      ],
    }),
    getCompanyLogoUrl: builder.query<Response<string>, number>({
      query: companyId => ({
        // Isolated presigned-URL endpoint — does NOT touch the company list query.
        url: `company/${companyId}/logoUrl`,
        method: 'GET',
      }),
    }),
    getCompanySize: builder.query<
      Response<{ id: number; size: string }[]>,
      void
    >({
      query: () => ({
        url: 'companySize',
        method: 'GET',
      }),
    }),
    getCompanyCategory: builder.query<
      Response<{ id: number; type: string }[]>,
      void
    >({
      query: () => ({
        url: 'companyCategory',
        method: 'GET',
      }),
    }),
    getSubscriptionPlans: builder.query<
      Response<{ id: number; planName: SubscriptionPlanName }[]>,
      void
    >({
      query: () => ({
        url: 'subscriptionPlan',
        method: 'GET',
      }),
    }),
    basicInfo: builder.query<Response<BasicInfo>, number>({
      query: companyId => ({
        // Root-level route on FetchCompanyController (no class-level prefix).
        url: `fetchBasicInfo/${companyId}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Company', id: 'basicInfo' }],
    }),
    createBasicInfo: builder.mutation<Response<BasicInfo>, FormData>({
      query: data => ({
        url: 'crossbuildz/basicInfo',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': undefined },
      }),
    }),
    updateBasicInfo: builder.mutation<Response<BasicInfo>, FormData>({
      query: data => ({
        // ContactInfoController/BasicInfoController are @RequestMapping("/crossbuildz").
        url: 'crossbuildz/basicInfo',
        method: 'PUT',
        body: data,
        headers: { 'Content-Type': undefined },
      }),
      invalidatesTags: [{ type: 'Company', id: 'basicInfo' }],
    }),
    contactInfo: builder.query<Response<ContactInfo[]>, number>({
      query: companyId => ({
        // FetchCompanyController has no class-level prefix, so this route is
        // root-level (cf. fetchCompanyTeamMember, called without a prefix).
        url: `fetchContactInfo/${companyId}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Company', id: 'contactInfo' }],
    }),
    createContactInfo: builder.mutation<
      Response<ContactInfo>,
      Omit<ContactInfo, 'companyBasicInfo' | 'id'> & {
        companyBasicInfo: { id: BasicInfo['id'] };
      }
    >({
      query: data => ({
        // ContactInfoController is @RequestMapping("/crossbuildz").
        url: 'crossbuildz/contactInfo',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Company', id: 'contactInfo' }],
    }),
    updateContactInfo: builder.mutation<Response<ContactInfo>, ContactInfo>({
      query: data => ({
        url: 'crossbuildz/contactInfo',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'Company', id: 'contactInfo' }],
    }),
    getAllTeamMembers: builder.query<
      Response<Sortable<TeamMember>>,
      { companyId: number; page: number; name: string }
    >({
      query: ({ companyId, page, name }) => ({
        url: `fetchCompanyTeamMember/${companyId}?page=${page}&size=10&name=${name}`,
        method: 'GET',
      }),
    }),
    getTeamMemberById: builder.query<Response<TeamMember>, number>({
      query: teamMemberId => ({
        url: `teamMember/${teamMemberId}`,
        method: 'GET',
      }),
    }),
    createTeamMember: builder.mutation<
      Response<TeamMember>,
      Omit<TeamMember, 'companyBasicInfo' | 'id' | 'status'> & {
        companyBasicInfo: { id: BasicInfo['id'] };
        role: { id: number };
      }
    >({
      query: data => ({
        url: 'crossbuildz/teamMember',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Company', id: 'teamMember' }],
    }),
    updateTeamMember: builder.mutation<Response<TeamMember>, TeamMember>({
      query: data => ({
        url: 'crossbuildz/teamMember',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'Company', id: 'teamMember' }],
    }),
    getAllRoles: builder.query<Response<Sortable<Role>>, number>({
      query: basicInfoId => ({
        url: `role?page=0&size=1000&`,
        method: 'GET',
        params: {
          page: 0,
          size: 1000,
          basicInfoId,
        },
      }),
    }),
    createSubscriptionPlan: builder.mutation<
      Response<SubscriptionPlan>,
      {
        companyBasicInfo: {
          id: BasicInfo['id'];
        };
        subscriptionPlan: {
          id: number;
        };
      }
    >({
      query: data => ({
        url: 'crossbuildz/subscriptionPlan',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Company', id: 'subscriptionPlan' }],
    }),
    subscriptionPlan: builder.query<Response<SubscriptionPlan>, number>({
      query: companyId => ({
        url: `fetchSubscriptionPlan/${companyId}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Company', id: 'subscriptionPlan' }],
    }),
    updateSubscriptionPlan: builder.mutation<
      Response<SubscriptionPlan>,
      SubscriptionPlan
    >({
      query: data => ({
        url: 'crossbuildz/subscriptionPlan',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Company', id: 'subscriptionPlan' }],
    }),
    toggleCompanyStatus: builder.mutation<
      Response<ChangedStatus>,
      {
        companyId: number;
        statusId: number;
      }
    >({
      query: data => ({
        url: 'company/changeStatus',
        method: 'POST',
        body: data,
      }),
    }),
    deleteCompany: builder.mutation<
      null,
      {
        companyId: number;
      }
    >({
      query: data => ({
        url: 'company',
        method: 'DELETE',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetAllCompaniesQuery,
  useGetCompanyByIdQuery,
  useGetCompanyLogoUrlQuery,
  useGetCompanySizeQuery,
  useGetCompanyCategoryQuery,
  useGetSubscriptionPlansQuery,
  useBasicInfoQuery,
  useCreateBasicInfoMutation,
  useUpdateBasicInfoMutation,
  useContactInfoQuery,
  useCreateContactInfoMutation,
  useUpdateContactInfoMutation,
  useGetAllTeamMembersQuery,
  useGetTeamMemberByIdQuery,
  useCreateTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useGetAllRolesQuery,
  useSubscriptionPlanQuery,
  useCreateSubscriptionPlanMutation,
  useUpdateSubscriptionPlanMutation,
  useToggleCompanyStatusMutation,
  useDeleteCompanyMutation,
} = companyApiSlice;
